/**
 * Extras Selection - Cart System with Smart Toolbar
 * Shows meals from previous step + extras added on this page
 */

(function() {
  'use strict';

  // Only run on extras page
  if (!document.querySelector('.extras-selection')) return;

  // ============================================
  // STATE
  // ============================================

  const state = {
    cartItems: {},       // { variantId: { quantity, image, title, isExtra } }
    extrasVariantIds: new Set(), // Variant IDs that are extras (from this page)
    productTitles: {},   // { variantId: title } - for tooltip on hover
    isProcessing: false
  };

  // ============================================
  // DOM CACHE
  // ============================================

  const dom = {
    cards: new Map(),      // variantId -> card element
    thumbnails: null,
    mealsCount: null,
    extrasCount: null,
    continueBtn: null
  };

  // ============================================
  // INIT
  // ============================================

  function init() {
    // Cache toolbar elements
    dom.thumbnails = document.getElementById('extras-toolbar-thumbnails');
    dom.mealsCount = document.getElementById('extras-toolbar-meals');
    dom.extrasCount = document.getElementById('extras-toolbar-extras');
    dom.continueBtn = document.getElementById('extras-toolbar-continue');

    // Cache all extras cards, mark their variant IDs as extras, and store titles
    document.querySelectorAll('.extras-card').forEach(card => {
      const variantId = card.dataset.variantId;
      if (variantId) {
        dom.cards.set(variantId, card);
        state.extrasVariantIds.add(variantId);
        // Cache product title for tooltip
        const titleEl = card.querySelector('.extras-card__title');
        if (titleEl) {
          state.productTitles[variantId] = titleEl.textContent.trim();
        }
      }
    });

    // Attach event listeners
    document.addEventListener('click', handleClick);

    if (dom.continueBtn) {
      dom.continueBtn.addEventListener('click', handleContinue);
    }

    // Sync with cart
    syncWithCart();

    console.log('[Extras Selection] Initialized');
  }

  // ============================================
  // EVENT HANDLING
  // ============================================

  function handleClick(e) {
    const button = e.target.closest('[data-action]');
    if (!button) return;

    const action = button.dataset.action;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    e.preventDefault();

    switch (action) {
      case 'add-extra':
        handleAdd(variantId, button);
        break;
      case 'increase-extra':
        handleIncrease(variantId, button);
        break;
      case 'decrease-extra':
        handleDecrease(variantId, button);
        break;
    }
  }

  function handleContinue() {
    dom.continueBtn.textContent = 'Loading...';
    window.location.href = '/cart';
  }

  // ============================================
  // CART OPERATIONS
  // ============================================

  function handleAdd(variantId, button) {
    button.classList.add('is-loading');
    button.disabled = true;

    addToCart(variantId, 1)
      .then(() => {
        if (!state.cartItems[variantId]) {
          state.cartItems[variantId] = { quantity: 0, image: null, title: '', isExtra: true };
        }
        state.cartItems[variantId].quantity += 1;
        state.cartItems[variantId].isExtra = true;

        // Get image and title from card
        const card = dom.cards.get(variantId);
        if (card) {
          const img = card.querySelector('.extras-card__image');
          if (img) state.cartItems[variantId].image = img.src;
          const titleEl = card.querySelector('.extras-card__title');
          if (titleEl) state.cartItems[variantId].title = titleEl.textContent.trim();
        }

        updateCardUI(variantId);
        updateToolbar();
      })
      .catch(err => {
        console.error('[Extras Selection] Add failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  }

  function handleIncrease(variantId, button) {
    button.classList.add('is-loading');

    addToCart(variantId, 1)
      .then(() => {
        state.cartItems[variantId].quantity += 1;
        updateCardUI(variantId);
        updateToolbar();
      })
      .catch(err => {
        console.error('[Extras Selection] Increase failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  }

  function handleDecrease(variantId, button) {
    const item = state.cartItems[variantId];
    if (!item || item.quantity <= 0) return;

    button.classList.add('is-loading');

    const newQty = item.quantity - 1;

    updateCartItem(variantId, newQty)
      .then(() => {
        if (newQty === 0) {
          delete state.cartItems[variantId];
        } else {
          state.cartItems[variantId].quantity = newQty;
        }
        updateCardUI(variantId);
        updateToolbar();
      })
      .catch(err => {
        console.error('[Extras Selection] Decrease failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  }

  // ============================================
  // CART API
  // ============================================

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: quantity }]
      })
    }).then(res => {
      if (!res.ok) throw new Error('Cart add failed');
      return res.json();
    });
  }

  function updateCartItem(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId.toString(),
        quantity: quantity
      })
    }).then(res => {
      if (!res.ok) throw new Error('Cart update failed');
      return res.json();
    });
  }

  function syncWithCart() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Hidden product IDs to skip
        const hiddenIds = ['40634459586612', '42152016642100', '6933132083252'];

        state.cartItems = {};

        cart.items.forEach(item => {
          // Skip hidden products
          if (hiddenIds.includes(item.variant_id.toString()) ||
              hiddenIds.includes(item.product_id.toString())) {
            return;
          }

          // Determine if this is an extra (from this page's collection)
          const isExtra = state.extrasVariantIds.has(item.variant_id.toString());

          state.cartItems[item.variant_id] = {
            quantity: item.quantity,
            image: item.image || null,
            title: item.product_title || '',
            isExtra: isExtra
          };
        });

        // Update all card UIs
        dom.cards.forEach((card, variantId) => {
          updateCardUI(variantId);
        });

        // Update toolbar
        updateToolbar();

        console.log('[Extras Selection] Synced with cart:', state.cartItems);
      })
      .catch(err => {
        console.error('[Extras Selection] Cart sync failed:', err);
      });
  }

  // ============================================
  // UI UPDATES
  // ============================================

  function updateCardUI(variantId) {
    const card = dom.cards.get(variantId);
    if (!card) return;

    const item = state.cartItems[variantId];
    const quantity = item ? item.quantity : 0;

    const addBtn = card.querySelector('.extras-card__add-btn');
    const qtySelector = card.querySelector('.extras-card__quantity-selector');
    const qtyCount = card.querySelector('.extras-card__qty-count');

    if (quantity > 0) {
      if (addBtn) addBtn.style.display = 'none';
      if (qtySelector) qtySelector.style.display = 'flex';
      if (qtyCount) qtyCount.textContent = quantity;
    } else {
      if (addBtn) addBtn.style.display = 'block';
      if (qtySelector) qtySelector.style.display = 'none';
    }
  }

  function updateToolbar() {
    let mealsCount = 0;
    let extrasCount = 0;
    const thumbnailsHtml = [];

    Object.entries(state.cartItems).forEach(([variantId, item]) => {
      if (item.isExtra) {
        extrasCount += item.quantity;
      } else {
        mealsCount += item.quantity;
      }

      // Add thumbnails (one per quantity)
      if (item.image) {
        const title = item.title || state.productTitles[variantId] || '';
        for (let i = 0; i < item.quantity; i++) {
          const typeClass = item.isExtra ? 'extras-toolbar__thumbnail--extra' : 'extras-toolbar__thumbnail--meal';
          const tooltipHtml = title ? `<span class="extras-toolbar__tooltip">${title}</span>` : '';
          thumbnailsHtml.push(`<span class="extras-toolbar__thumb-wrap">${tooltipHtml}<img src="${item.image}" alt="${title}" class="extras-toolbar__thumbnail ${typeClass}"></span>`);
        }
      }
    });

    // Update counts
    if (dom.mealsCount) dom.mealsCount.textContent = mealsCount;
    if (dom.extrasCount) dom.extrasCount.textContent = extrasCount;

    // Update thumbnails
    if (dom.thumbnails) {
      if (thumbnailsHtml.length > 0) {
        dom.thumbnails.innerHTML = thumbnailsHtml.join('');
      } else {
        dom.thumbnails.innerHTML = '<span class="extras-toolbar__empty-hint">Your items will appear here</span>';
      }
    }
  }

  // ============================================
  // STARTUP
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
